import { SEO } from '@/components/SEO'

export default function TermsAndConditions() {
  return (
    <>
      <SEO
        title="Terms and Conditions - WithWords"
        description="Read our Terms and Conditions to understand the rules and guidelines for using WithWords services."
      />
      
      <div className="container py-8 max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-6">Terms and Conditions</h1>
        <p className="text-sm text-muted-foreground mb-8">Last Updated: April 8, 2025</p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
          <p className="mb-4">
            Welcome to WithWords, operated by AI Labs Group LLC ("we," "our," or "us"), a company based in the state of Georgia.
            These Terms and Conditions ("Terms") govern your access to and use of our website, applications, and services.
          </p>
          <p className="mb-4">
            Please read these Terms carefully before using our services. By accessing or using our services, you agree to be bound by these Terms.
            If you do not agree to these Terms, you may not access or use our services.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Definitions</h2>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>"Services"</strong> refers to the WithWords website, applications, and any other products or services offered by AI Labs Group LLC.
            </li>
            <li>
              <strong>"User"</strong> refers to any individual who accesses or uses our Services.
            </li>
            <li>
              <strong>"Content"</strong> refers to any information, text, graphics, photos, or other materials uploaded, downloaded, or appearing on our Services.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Account Registration and Requirements</h2>
          <p className="mb-4">
            To access certain features of our Services, you may be required to register for an account. When you register, you agree to provide accurate, current, and complete information about yourself and to update this information to keep it accurate, current, and complete.
          </p>
          <p className="mb-4">
            You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account or any other breach of security.
          </p>
          <p className="mb-4">
            We reserve the right to disable any user account at any time, including if we believe that you have violated these Terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">User Obligations and Prohibited Uses</h2>
          <p className="mb-4">You agree not to use our Services to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Violate any applicable law, regulation, or these Terms</li>
            <li>Infringe the intellectual property rights of others</li>
            <li>Transmit any material that is defamatory, offensive, or otherwise objectionable</li>
            <li>Interfere with or disrupt the integrity or performance of our Services</li>
            <li>Attempt to gain unauthorized access to our Services or related systems</li>
            <li>Collect or store personal data about other users without their consent</li>
            <li>Impersonate any person or entity or falsely state or otherwise misrepresent your affiliation with a person or entity</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Intellectual Property Rights</h2>
          <p className="mb-4">
            Our Services and their contents, features, and functionality are owned by AI Labs Group LLC and are protected by copyright, trademark, and other intellectual property laws.
          </p>
          <p className="mb-4">
            You may not copy, modify, create derivative works of, publicly display, publicly perform, republish, or transmit any of the material on our Services without our prior written consent.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">User-Generated Content</h2>
          <p className="mb-4">
            Our Services may allow you to post, link, store, share, or otherwise make available certain information, text, graphics, videos, or other material. You retain ownership rights in such content, but you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute such content in connection with our Services.
          </p>
          <p className="mb-4">
            You represent and warrant that you own or have the necessary rights to post any content you submit through our Services, and that such content does not violate the rights of any third party.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Limitation of Liability</h2>
          <p className="mb-4">
            To the maximum extent permitted by law, AI Labs Group LLC and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Your access to or use of or inability to access or use our Services</li>
            <li>Any conduct or content of any third party on our Services</li>
            <li>Any content obtained from our Services</li>
            <li>Unauthorized access, use, or alteration of your transmissions or content</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Disclaimer of Warranties</h2>
          <p className="mb-4">
            Our Services are provided on an "as is" and "as available" basis, without any warranties of any kind, either express or implied. AI Labs Group LLC disclaims all warranties, including but not limited to, implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
          </p>
          <p className="mb-4">
            We do not warrant that our Services will be uninterrupted or error-free, that defects will be corrected, or that our Services or the server that makes them available are free of viruses or other harmful components.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Indemnification</h2>
          <p className="mb-4">
            You agree to indemnify, defend, and hold harmless AI Labs Group LLC and its officers, directors, employees, and agents from and against any and all claims, liabilities, damages, losses, or expenses, including reasonable attorneys' fees and costs, arising out of or in any way connected with your access to or use of our Services or your violation of these Terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Termination</h2>
          <p className="mb-4">
            We may terminate or suspend your account and access to our Services immediately, without prior notice or liability, for any reason, including without limitation if you breach these Terms.
          </p>
          <p className="mb-4">
            Upon termination, your right to use our Services will immediately cease. If you wish to terminate your account, you may simply discontinue using our Services or contact us to request account deletion.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Governing Law</h2>
          <p className="mb-4">
            These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, without regard to its conflict of law provisions.
          </p>
          <p className="mb-4">
            Any legal action or proceeding arising out of or relating to these Terms or your use of our Services shall be brought exclusively in the state or federal courts located in the State of Georgia, and you consent to the personal jurisdiction of such courts.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Changes to These Terms</h2>
          <p className="mb-4">
            We may update these Terms from time to time. We will notify you of any changes by posting the new Terms on this page and updating the "Last Updated" date at the top of these Terms.
          </p>
          <p className="mb-4">
            You are advised to review these Terms periodically for any changes. Changes to these Terms are effective when they are posted on this page. Your continued use of our Services following the posting of revised Terms means that you accept and agree to the changes.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Severability</h2>
          <p className="mb-4">
            If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall be enforced.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Entire Agreement</h2>
          <p className="mb-4">
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and AI Labs Group LLC regarding our Services and supersede all prior agreements and understandings.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <p className="mb-4">
            If you have any questions about these Terms, please contact us at:
          </p>
          <p className="text-muted-foreground">
            AI Labs Group LLC<br />
            Email: legal@withwords.app
          </p>
        </section>
      </div>
    </>
  )
}